import { useEffect, useState } from "react";
import emailjs from "emailjs-com";
import "./ConsultationPopup.css";

let popupAlreadyShown = false;

const EMAILJS_SERVICE_ID = "service_d5ju7td";
const EMAILJS_PUBLIC_KEY  = "3DuqRUkxLl2usJwy1";
const TEAM_TEMPLATE_ID    = "template_j7urv1v";
const USER_TEMPLATE_ID    = "template_mxq2stz";

const INITIAL_FORM = {
  name: "",
  mobile: "",
  email: "",
  location: "",
  constructionType: "",
  timeline: "",
};

function ConsultationPopup() {
  const [showPopup, setShowPopup]       = useState(false);
  const [formData, setFormData]         = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(""); // "" | "success" | "error"
  const [errors, setErrors]             = useState({});

  // Auto-show after 5 s
  useEffect(() => {
    if (popupAlreadyShown) return;
    const timer = setTimeout(() => {
      popupAlreadyShown = true;
      setShowPopup(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = showPopup ? "hidden" : "auto";
    return () => { document.body.style.overflow = "auto"; };
  }, [showPopup]);

  // Escape key
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closePopup(); };
    if (showPopup) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showPopup]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim())                e.name   = "Name is required";
    if (!/^[0-9]{10}$/.test(formData.mobile)) e.mobile = "Enter a valid 10-digit mobile number";
    if (!/\S+@\S+\.\S+/.test(formData.email)) e.email  = "Enter a valid email address";
    return e;
  };

  const closePopup = () => {
    setShowPopup(false);
    setFormData(INITIAL_FORM);
    setSubmitStatus("");
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      // 1. Notify the team at build@sarrathiconstructions.com
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        TEAM_TEMPLATE_ID,
        {
          request_type: "New Consultation Request",
          from_name:    formData.name,
          from_email:   formData.email,
          phone:        formData.mobile,
          field1_label: "Construction Type",
          field1_value: formData.constructionType || "Not specified",
          field2_label: "Location",
          field2_value: formData.location || "Not specified",
          field3_label: "Timeline",
          field3_value: formData.timeline || "Not specified",
          extra_details: "",
        },
        EMAILJS_PUBLIC_KEY
      );

      // 2. Send confirmation to the customer
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        USER_TEMPLATE_ID,
        {
          to_email:          formData.email,
          user_name:         formData.name,
          user_phone:        formData.mobile,
          construction_type: formData.constructionType || "Not specified",
          project_location:  formData.location         || "Not specified",
          timeline:          formData.timeline          || "Not specified",
        },
        EMAILJS_PUBLIC_KEY
      );

      setSubmitStatus("success");
      setFormData(INITIAL_FORM);
      setTimeout(closePopup, 4000);
    } catch (err) {
      console.error("EmailJS error:", err);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showPopup) return null;

  return (
    <div className="consultation-overlay" onClick={closePopup}>
      <div className="consultation-popup" onClick={(e) => e.stopPropagation()}>

        <button className="popup-close" onClick={closePopup} aria-label="Close">×</button>

        {submitStatus === "success" ? (
          <div className="popup-status success">
            <div className="success-icon-wrap">
              <svg viewBox="0 0 24 24">
                <path d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="success-title">Request Sent Successfully!</h3>
            <p className="success-msg">
              Thank you for reaching out to us! Our technical team will
              contact you within <strong>24 hours</strong> to discuss your
              project requirements.
            </p>
            <span className="success-detail">
              Confirmation sent to your email
            </span>
          </div>
        ) : (
          <>
            <h2>Let's Build Your Dream Project</h2>
            <br/>
            <form onSubmit={handleSubmit} noValidate>
              <div className="forms-grid">

                <div className="field-wrap">
                  <input
                    type="text"
                    name="name"
                    placeholder="Full Name *"
                    value={formData.name}
                    onChange={handleChange}
                    className={errors.name ? "input-error" : ""}
                  />
                  {errors.name && <span className="field-error">{errors.name}</span>}
                </div>

                <div className="field-wrap">
                  <input
                    type="tel"
                    name="mobile"
                    placeholder="Mobile Number *"
                    value={formData.mobile}
                    onChange={handleChange}
                    maxLength={10}
                    className={errors.mobile ? "input-error" : ""}
                  />
                  {errors.mobile && <span className="field-error">{errors.mobile}</span>}
                </div>

                <div className="field-wrap">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? "input-error" : ""}
                  />
                  {errors.email && <span className="field-error">{errors.email}</span>}
                </div>

                <div className="field-wrap">
                  <input
                    type="text"
                    name="location"
                    placeholder="Project Location"
                    value={formData.location}
                    onChange={handleChange}
                  />
                </div>

                <div className="field-wrap">
                  <select
                    name="constructionType"
                    value={formData.constructionType}
                    onChange={handleChange}
                  >
                    <option value="">Construction Type</option>
                    <option value="Residential">Residential</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Renovation">Renovation</option>
                  </select>
                </div>

                <div className="field-wrap">
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                  >
                    <option value="">When do you plan to build?</option>
                    <option value="Immediately">Immediately</option>
                    <option value="Within 3 Months">Within 3 Months</option>
                    <option value="Within 6 Months">Within 6 Months</option>
                    <option value="Later">Later</option>
                  </select>
                </div>

              </div>

              {submitStatus === "error" && (
                <div className="popup-status error">
                  Something went wrong. Please try again or call us directly.
                </div>
              )}

              <button type="submit" className="popup-submit" disabled={isSubmitting}>
                {isSubmitting ? "Sending…" : "Request Free Consultation"}
              </button>
            </form>
          </>
        )}

      </div>
    </div>
  );
}

export default ConsultationPopup;